import type { RefObject } from 'react'
import { useMemo } from 'react'

import { useComputedStyle } from './use-computed-style'
import { useContainerWidth } from './use-container-width'

interface UseTruncateTextConfig {
  text: string
  numberOfLines: number
  /** If true, the text will be truncated to the nearest whole word. Otherwise, it will be truncated to the nearest character. */
  shouldTruncateWholeWords?: boolean
  containerElement?: RefObject<HTMLElement | null>
  overflowElement?: RefObject<HTMLElement | null>
}

function getTextWidth(text: string, font: string) {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) {
    return 0
  }
  context.font = font
  const metrics = context.measureText(text)
  return metrics.width
}

/** Truncate text to a given number of lines. Accounts for gap caused by text wrapping. */
function getTruncatedTextByLines(
  text: string,
  containerWidth: number,
  font: string,
  letterSpacing = 0,
  shouldTruncateWholeWords = false,
  overflowWidth = 0,
  numberOfLines = 1,
) {
  let currentText = text
  let truncatedText = ''
  const truncatedTextArray: string[] = []

  while (numberOfLines > 0) {
    // For each line, truncate the text to the nearest whole word.
    let truncatedLine = ''

    if (numberOfLines === 1) {
      truncatedLine = getTruncatedText(
        currentText,
        containerWidth,
        font,
        letterSpacing,
        shouldTruncateWholeWords,
        overflowWidth,
      )
    } else {
      truncatedLine = getTruncatedText(currentText, containerWidth, font, letterSpacing, true, 0)
    }

    // Add this truncated line to the final text.
    truncatedText += truncatedLine
    truncatedTextArray.push(truncatedLine)
    // Remove the truncated line from the current text.
    currentText = currentText.substring(truncatedLine.length)

    numberOfLines--
  }

  return { truncatedText, truncatedTextArray }
}

/** Truncate text to a given width. */
function getTruncatedText(
  text: string,
  width: number,
  font: string,
  letterSpacing = 0,
  shouldTruncateWholeWords = false,
  overflowWidth = 0,
) {
  let truncatedText = text

  const totalWidth = getTextWidth(text, font) + letterSpacing * (text.length - 1)

  // If the text is too wide, truncate it.
  if (totalWidth > width - overflowWidth) {
    //Binary search to find the optimal truncation point
    let left = 0
    let right = text.length

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      const currentWidth = getTextWidth(text.substring(0, mid), font) + letterSpacing * (mid - 1)

      if (currentWidth <= width - overflowWidth) {
        left = mid + 1
        truncatedText = text.substring(0, Math.max(0, mid))
      } else {
        right = mid - 1
      }
    }
  }

  if (shouldTruncateWholeWords) {
    // remove last word if it's too long
    const nextChar = text[truncatedText.length]
    if (nextChar !== undefined && nextChar !== ' ') {
      const lastSpaceIndex = truncatedText.lastIndexOf(' ')
      if (lastSpaceIndex !== -1) {
        truncatedText = truncatedText.substring(0, lastSpaceIndex)
      }
    }
  }

  return truncatedText
}

/**
 * Hook to truncate text to a given number of lines. Given a container element, it will truncate the text to fit within the container.
 *
 * If space for a custom text overflow component is needed, a padding element can be provided. This will reserve space for the overflow component.
 */
export function useTruncateText({
  text,
  shouldTruncateWholeWords = false,
  numberOfLines = 1,
  containerElement,
  overflowElement,
}: UseTruncateTextConfig) {
  const containerWidth = Math.ceil(useContainerWidth({ containerElement, boxType: 'content-box' }))
  const overflowWidth = Math.ceil(
    useContainerWidth({ containerElement: overflowElement, boxType: 'border-box', defaultWidth: 0 }),
  )

  const computedStyle = useComputedStyle({ htmlElement: containerElement })

  const fontSize = computedStyle?.fontSize || '14px'
  const fontFamily = computedStyle?.fontFamily || '"Plus Jakarta Sans", Arial, sans-serif'

  const { truncatedText, truncatedTextArray } = useMemo(() => {
    const font = fontSize + ' ' + fontFamily
    return getTruncatedTextByLines(
      text,
      containerWidth,
      font,
      0,
      shouldTruncateWholeWords,
      overflowWidth,
      numberOfLines,
    )
  }, [text, containerWidth, fontSize, fontFamily, shouldTruncateWholeWords, overflowWidth, numberOfLines])

  return { truncatedText, truncatedTextArray }
}
